import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellOff } from "lucide-react";
import { WeeklyLetterCard } from "@/components/WeeklyLetterCard";
import { PartnerShareCard } from "@/components/PartnerShareCard";

const LAST_SHOWN_KEY = "meridia:lastReminderShown";

export function useEveningReminder() {
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        const { data } = await api.get("/user/settings");
        if (cancelled) return;
        if (!data.reminder_enabled) return;
        if (typeof Notification === "undefined" || Notification.permission !== "granted") return;

        const check = () => {
          const now = new Date();
          const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
          const today = now.toISOString().slice(0, 10);
          const last = localStorage.getItem(LAST_SHOWN_KEY);
          if (hhmm === data.reminder_time && last !== today) {
            try {
              new Notification("A moment with Meridia", {
                body: "Two minutes of noticing. How did today meet you?",
                icon: "/favicon.ico",
                tag: "meridia-evening",
              });
              localStorage.setItem(LAST_SHOWN_KEY, today);
            } catch {}
          }
        };
        check();
        timer.current = setInterval(check, 30 * 1000);
      } catch {}
    };
    start();
    return () => {
      cancelled = true;
      if (timer.current) clearInterval(timer.current);
    };
  }, []);
}

export default function Settings() {
  const [enabled, setEnabled] = useState(false);
  const [time, setTime] = useState("20:30");
  const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; } })();
  const [letterCfg, setLetterCfg] = useState({ weekly_letter_enabled: false, letter_day: 0, letter_hour: 17, letter_timezone: browserTz });
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/user/settings");
        setEnabled(data.reminder_enabled);
        setTime(data.reminder_time);
        setLetterCfg({
          weekly_letter_enabled: !!data.weekly_letter_enabled,
          letter_day: data.letter_day ?? 0,
          letter_hour: data.letter_hour ?? 17,
          letter_timezone: data.letter_timezone || browserTz,
        });
      } catch {}
    })();
  }, []);

  const requestPermission = async () => {
    if (typeof Notification === "undefined") return;
    const p = await Notification.requestPermission();
    setPermission(p);
    if (p !== "granted") {
      toast.error("Reminders need notification permission. You can change this in your browser settings.");
    }
  };

  const save = async (overrides = {}) => {
    setSaving(true);
    try {
      if (enabled && permission !== "granted") {
        await requestPermission();
      }
      await api.put("/user/settings", { reminder_enabled: enabled, reminder_time: time, ...letterCfg, ...overrides });
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const testIt = () => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") {
      toast.error("Allow notifications first.");
      return;
    }
    new Notification("A moment with Meridia", {
      body: "This is what your evening reminder will feel like.",
      tag: "meridia-test",
    });
  };

  return (
    <div className="max-w-2xl mx-auto rise">
      <p className="text-xs uppercase tracking-[0.22em] text-[#8C7A70] font-semibold">Settings</p>
      <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl text-[#2C221E] leading-tight" data-testid="settings-title">
        A gentle rhythm
      </h1>
      <p className="mt-3 text-[#52443E]">
        A small chime at the end of the day, a letter on Sundays, and a glance you can hand to someone who loves you.
      </p>

      <div className="mt-10 card-paper p-6 sm:p-8" data-testid="settings-reminder-card">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-full bg-[#F9ECE8] flex items-center justify-center text-[#C26D53]">
              {enabled ? <Bell size={18} /> : <BellOff size={18} />}
            </div>
            <div>
              <h2 className="font-serif-display text-xl text-[#2C221E]">Evening reminder</h2>
              <p className="text-sm text-[#73635B] mt-1 max-w-sm">
                A single notification at your chosen time, only when you haven't checked in yet today.
              </p>
            </div>
          </div>
          <label className="inline-flex items-center cursor-pointer gap-3">
            <span className="text-sm text-[#52443E]">{enabled ? "On" : "Off"}</span>
            <input
              type="checkbox"
              className="sr-only peer"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              data-testid="settings-reminder-toggle"
            />
            <span className="relative w-11 h-6 bg-[#EAE0D2] peer-checked:bg-[#C26D53] rounded-full transition-colors
                             after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border after:border-[#D8CBB9]
                             after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
          </label>
        </div>

        <div className="mt-6 flex items-center gap-4 flex-wrap">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8C7A70] font-semibold">Reminder time</label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="input-paper mt-2 max-w-[160px]"
              disabled={!enabled}
              data-testid="settings-reminder-time"
            />
          </div>
          {permission !== "granted" && enabled && (
            <button className="btn-ghost mt-6" onClick={requestPermission} data-testid="settings-permission-request">
              Allow notifications
            </button>
          )}
          <button className="btn-ghost mt-6" onClick={testIt} data-testid="settings-test-reminder">
            Send a test
          </button>
        </div>

        <div className="mt-7 flex items-center justify-end">
          <button className="btn-primary" onClick={() => save()} disabled={saving} data-testid="settings-save">
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>

        <p className="mt-4 text-xs text-[#8C7A70]">
          Reminders fire while Meridia is open in your browser. For a stronger reminder, keep this tab open or add Meridia to your home screen.
        </p>
      </div>

      <WeeklyLetterCard
        cfg={letterCfg}
        onChange={(patch) => { setLetterCfg((c) => ({ ...c, ...patch })); save(patch); }}
      />
      <PartnerShareCard />
    </div>
  );
}
